import { FlatList, ListRenderItemInfo, StyleSheet, View, ViewProps } from 'react-native'
import { Color, PopUp, Spacing, Text } from 'zens-storybook'

import { Comment, Photo, useComments, usePhotos } from 'zens-store'

import FeedbackRow from './row'
import FeedbackPhoto from './photo'
import FeedbackEmpty from './empty'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  separator: {
    height: 1,
    backgroundColor: Color.calmGrey,
  },
  popup: {
    maxWidth: 800,
    padding: 0,
  },
  comment: {
    margin: Spacing.M,
  },
  skeletonDate: {
    maxWidth: 200,
    marginBottom: Spacing.S,
  },
})

interface Props extends ViewProps {
  companyId: number
  productId?: number
  workspaceId?: number
  commentId?: number
  photoId?: number
  onSelect?: (item?: { commentId?: number; photoId?: number }) => void
}

const FeedbackTable = (props: Props) => {
  const { companyId, productId, workspaceId, commentId, photoId, onSelect, style, ...otherProps } = props

  const photos = usePhotos({ companyId, productId, workspaceId })
  const comments = useComments({ companyId, productId, workspaceId })

  const photo = photos.data?.find((item) => item.id === photoId)

  function keyExtractor(item: Comment | Photo) {
    return item instanceof Comment ? `comment-${item.id}` : `photo-${item.id}`
  }

  function show(item: Comment | Photo) {
    onSelect?.(
      item instanceof Comment ? { commentId: item.id, photoId: undefined } : { photoId: item.id, commentId: undefined },
    )
  }

  function renderItem(info: ListRenderItemInfo<Comment | Photo>) {
    return <FeedbackRow data={info.item} onPress={() => show(info.item)} />
  }

  const rows = [...(photos.data || []), ...(comments.data || [])].sort(
    (a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0),
  )

  return (
    <>
      <FlatList
        data={rows}
        renderItem={renderItem}
        ItemSeparatorComponent={() => <View style={styles.separator} />}
        keyExtractor={keyExtractor}
        style={[styles.container, style]}
        ListEmptyComponent={<FeedbackEmpty />}
        {...otherProps}
      />
      <PopUp visible={!!commentId || !!photoId} onClose={() => onSelect?.()} style={styles.popup}>
        {commentId ? (
          <FeedbackRow
            data={comments.data?.find((item) => item.id === commentId) as Comment}
            style={styles.comment}
            showAll
            disabled
          />
        ) : photo ? (
          <FeedbackPhoto photo={photo} />
        ) : null}
      </PopUp>
    </>
  )
}

interface SkeletonProps extends ViewProps {
  numberOfRows?: number
}

FeedbackTable.Skeleton = (props: SkeletonProps) => {
  const { numberOfRows = 3, style, ...otherProps } = props

  return (
    <FlatList
      style={[styles.container, style]}
      data={Array.from({ length: numberOfRows })}
      renderItem={() => (
        <View style={styles.comment}>
          <Text.Skeleton style={styles.skeletonDate} />
          <Text.Skeleton numberOfLines={2} />
        </View>
      )}
      ItemSeparatorComponent={() => <View style={styles.separator} />}
      {...otherProps}
    />
  )
}

export default FeedbackTable
